import { useEffect } from 'react';

/**
 * Custom hook to persist form state in sessionStorage using react-hook-form's `watch`.
 * It also handles data expiration using a time-to-live (TTL) mechanism.
 *
 * @param {string} key           Storage key to use in sessionStorage
 * @param {object} methods       React Hook Form methods (should include `watch`)
 * @param {number} expiryHours   Number of hours before data is considered stale (default: 24)
 * @returns {object}             Object containing `getValidData()` to retrieve the hydrated form state
 */
export function useSessionFormPersistence(key, methods, expiryHours = 24) {
  const { watch } = methods;

  // Automatically persist form changes to sessionStorage on any value change
  useEffect(() => {
    const sub = watch((value) => {
      sessionStorage.setItem(key, JSON.stringify({ ...value, __savedAt: Date.now() }));
    });
    return () => sub.unsubscribe(); // Cleanup on unmount
  }, [watch, key]);

  /**
   * Retrieves the saved form data if it's still valid based on TTL.
   * Returns `null` if the data is missing or expired.
   */
  const getValidData = () => {
    const raw = JSON.parse(sessionStorage.getItem(key) || 'null');
    const isExpired = raw && Date.now() - raw.__savedAt > expiryHours * 3600 * 1000;
    return !raw || isExpired ? null : raw;
  };

  return { getValidData };
}
